import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const EXIT_MS = 320;

/**
 * Animated popup / bottom sheet.
 *  - phones: slides up from the bottom like a native sheet, can be dragged down by its handle to close
 *  - desktop: rises + scales in with a soft spring "landing"
 *  - stays mounted while it plays its exit animation (a plain `{open && <div/>}` just vanishes)
 *  - page scroll is locked with pure CSS (`html:has(.navi-modal-root)`), so it can never get stuck
 *  - honours prefers-reduced-motion (fade only)
 * Give elements inside it className="navi-rise" + style={{"--i": n}} for a staggered fade-up.
 */
export default function SmoothModal({
  open, onClose, children, z = 80, maxWidth = "sm:max-w-lg", panelClassName = "",
  backdropClose = true, labelledBy, testId, backdropTestId,
}) {
  const [mounted, setMounted] = useState(!!open);
  const [shown, setShown] = useState(false);
  const kept = useRef(children);
  const panelRef = useRef(null);
  const lastFocus = useRef(null);
  const drag = useRef({ id: null, startY: 0, dy: 0, t: 0 });
  if (open) kept.current = children;   // keep the last content on screen while the exit animation plays

  useEffect(() => {
    if (open) {
      lastFocus.current = document.activeElement;
      if (panelRef.current) panelRef.current.style.transform = "";   // clear a previous drag
      setMounted(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setShown(true)); });
      return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS + 60);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (shown && open) panelRef.current?.focus({ preventScroll: true });
    if (!open && !mounted) { try { lastFocus.current?.focus?.({ preventScroll: true }); } catch { /* element gone */ } }
  }, [shown, open, mounted]);

  if (!mounted || typeof document === "undefined") return null;

  const onDown = (e) => {
    if (e.pointerType === "mouse") return;
    drag.current = { id: e.pointerId, startY: e.clientY, dy: 0, t: performance.now() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (panelRef.current) panelRef.current.style.transition = "none";
  };
  const onMove = (e) => {
    const d = drag.current;
    if (d.id !== e.pointerId || !panelRef.current) return;
    d.dy = Math.max(0, e.clientY - d.startY);
    panelRef.current.style.transform = `translate3d(0,${d.dy}px,0)`;
  };
  const onUp = (e) => {
    const d = drag.current;
    if (d.id !== e.pointerId || !panelRef.current) return;
    d.id = null;
    const el = panelRef.current;
    const velocity = d.dy / Math.max(1, performance.now() - d.t);
    el.style.transition = "";
    if (d.dy > 110 || velocity > 0.6) {
      el.style.transform = "translate3d(0,102%,0)";
      onClose?.();
    } else {
      el.style.transform = "";
    }
  };

  const state = open ? (shown ? "open" : "pre") : "closed";
  return createPortal(
    <div className="navi-modal-root" data-state={state} style={{ zIndex: z }} role="dialog" aria-modal="true" aria-labelledby={labelledBy} data-testid={testId}>
      <div className="navi-modal-backdrop" data-testid={backdropTestId} onMouseDown={backdropClose ? () => onClose?.() : undefined} />
      <div ref={panelRef} tabIndex={-1} className={`navi-modal-panel ${maxWidth} ${panelClassName}`}>
        <div className="navi-modal-grab" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} aria-hidden="true"><span /></div>
        {kept.current}
      </div>
    </div>,
    document.body
  );
}
